import type { AdminRole, AdminUser } from '@/types'

type RoleAssignableUser = Partial<AdminUser> & {
  roleId?: string | null
  roleCode?: string | null
}

const roleHierarchy: Record<string, number> = {
  arl_superadmin: 100,
  arl_admin: 80,
  arl_operator: 60,
  arl_support: 40,
  arl_viewer: 20,
  superadmin: 100,
  admin: 80,
  operator: 60,
  support: 40,
  viewer: 20,
}

const normalizeRoleIdentifier = (value?: string | null): string =>
  value?.trim().toLowerCase().replace(/-/g, '_') ?? ''

const getRoleLevel = (...values: Array<string | null | undefined>): number =>
  values.reduce((level, value) => {
    if (level > 0) return level
    const normalized = normalizeRoleIdentifier(value)
    return roleHierarchy[normalized] ?? 0
  }, 0)

const isSuperadminRole = (role: AdminRole): boolean =>
  normalizeRoleIdentifier(role.id) === 'arl_superadmin'
  || normalizeRoleIdentifier(role.code) === 'superadmin'

export const getAssignableAdminRoles = (
  roles: AdminRole[] | undefined,
  currentUser: RoleAssignableUser | null | undefined
): AdminRole[] => {
  if (!roles || !Array.isArray(roles) || !currentUser) {
    return []
  }

  const currentUserLevel = getRoleLevel(
    currentUser.role_id,
    currentUser.roleId,
    currentUser.role_code,
    currentUser.roleCode
  )

  return roles.filter(role => {
    if (!role.is_active) return false
    if (isSuperadminRole(role)) return false
    const roleLevel = getRoleLevel(role.id, role.code)
    return roleLevel < currentUserLevel
  })
}
